import env from "./env";

const toBuffer = (value) => {
  const base64 = value.replace(/-/g, "+").replace(/_/g, "/");
  const binary = atob(base64.padEnd(Math.ceil(base64.length / 4) * 4, "="));
  return Uint8Array.from(binary, (char) => char.charCodeAt(0)).buffer;
};

const toBase64Url = (buffer) => {
  const binary = String.fromCharCode(...new Uint8Array(buffer));
  return btoa(binary).replace(/\+/g, "-").replace(/\//g, "_").replace(/=+$/, "");
};

export const passkeysSupported = () =>
  typeof window !== "undefined" && !!window.PublicKeyCredential && !!navigator.credentials;

const request = async (path, { method = "GET", body, auth } = {}) => {
  const token = localStorage.getItem("token");
  const response = await fetch(`${env.url}${path}`, {
    method,
    headers: {
      Accept: "application/json",
      ...(body ? { "Content-Type": "application/json" } : {}),
      ...(auth && token ? { Authorization: `Bearer ${token}` } : {}),
    },
    body: body ? JSON.stringify(body) : undefined,
  });
  let json = {};
  try {
    json = await response.json();
  } catch {
    json = {};
  }
  if (!response.ok || json?.success === false) {
    throw new Error(json?.message || "Passkey request failed. Please try again.");
  }
  return json;
};

const friendlyError = (error) => {
  if (error?.name === "NotAllowedError") return new Error("Passkey request was cancelled or timed out.");
  if (error?.name === "InvalidStateError") return new Error("This device already has a passkey for your account.");
  return error;
};

export const listPasskeys = () => request("/passkeys", { auth: true }).then((res) => res.data || []);

export const deletePasskey = (id) => request(`/passkeys/${id}`, { method: "DELETE", auth: true });

export const registerPasskey = async (name) => {
  if (!passkeysSupported()) throw new Error("This browser doesn't support passkeys.");
  const { data } = await request("/passkeys/register/options", { method: "POST", body: {}, auth: true });
  const options = data.publicKey;

  let credential;
  try {
    credential = await navigator.credentials.create({
      publicKey: {
        ...options,
        challenge: toBuffer(options.challenge),
        user: { ...options.user, id: toBuffer(options.user.id) },
        excludeCredentials: (options.excludeCredentials || []).map((item) => ({ ...item, id: toBuffer(item.id) })),
      },
    });
  } catch (error) {
    throw friendlyError(error);
  }

  return request("/passkeys/register", {
    method: "POST",
    auth: true,
    body: {
      name,
      clientDataJSON: toBase64Url(credential.response.clientDataJSON),
      attestationObject: toBase64Url(credential.response.attestationObject),
    },
  });
};

// Resolves with the login API response (same shape as the password login).
export const loginWithPasskey = async () => {
  if (!passkeysSupported()) throw new Error("This browser doesn't support passkeys.");
  const { data } = await request("/passkeys/login/options", { method: "POST", body: {} });
  const options = data.options.publicKey;

  let assertion;
  try {
    assertion = await navigator.credentials.get({
      publicKey: {
        ...options,
        challenge: toBuffer(options.challenge),
        allowCredentials: (options.allowCredentials || []).map((item) => ({ ...item, id: toBuffer(item.id) })),
      },
    });
  } catch (error) {
    throw friendlyError(error);
  }

  return request("/passkeys/login", {
    method: "POST",
    body: {
      challenge_id: data.challenge_id,
      id: toBase64Url(assertion.rawId),
      clientDataJSON: toBase64Url(assertion.response.clientDataJSON),
      authenticatorData: toBase64Url(assertion.response.authenticatorData),
      signature: toBase64Url(assertion.response.signature),
    },
  });
};
